import { createRoute, Navigate } from "@tanstack/react-router";
import { useRef } from "react";
import { useSession } from "../auth-client";
import { Route as rootRoute } from "./__root";
import { LoadingScreen } from "@/frontend/components/onboarding/Onboarding";
import { AuthenticatedView } from "@/frontend/components/index/AuthenticatedView";
import { SplitGridLoginView } from "@/frontend/components/index/SplitGridLoginView";
import { useActorContextQuery } from "@/frontend/queries/index";
import { hasOAuthRequest, resumeOAuthAuthorize } from "@/frontend/utils/auth";
import { Loader2 } from "lucide-react";

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: (search.redirect as string) || "",
    patient_session_id: typeof search.patient_session_id === "string" ? search.patient_session_id : undefined,
  }),
  component: HomePage,
});

function HomePage() {
  const { redirect, patient_session_id } = Route.useSearch();
  const { data: session, isPending } = useSession();
  const actorContextQuery = useActorContextQuery(!!session);
  // Better Auth's useSession flips isPending back to true on tab-focus
  // refetches when data is null; without this guard the LoadingScreen would
  // unmount SplitGridLoginView mid-OTP-flow every time the user pops over
  // to their inbox.
  const hasResolvedSession = useRef(false);
  if (!isPending) hasResolvedSession.current = true;

  if (isPending && !hasResolvedSession.current) {
    return <LoadingScreen />;
  }

  if (session) {
    // Resume an in-flight OAuth authorize once authenticated (e.g. after a social-login round-trip returns here).
    if (hasOAuthRequest()) {
      resumeOAuthAuthorize();
      return (
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (actorContextQuery.isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      );
    }

    const isPlatformAdmin = session.user.role === "admin";
    const actorType = actorContextQuery.data?.actorType ?? "orgMember";

    if (isPlatformAdmin || actorType === "orgMember") {
      const doRedirect = redirect && redirect.startsWith("/");
      const redirectUrl = doRedirect ? redirect : "/dashboard/patients";
      if (doRedirect) {
        window.location.href = redirectUrl;
        return (
          <div className="min-h-screen flex items-center justify-center">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        );
      }
      return <Navigate to="/dashboard/patients" search={{ patientMembershipId: undefined }} />;
    }

    return <AuthenticatedView session={session} patientSessionId={patient_session_id} />;
  }

  // Not authenticated — show split grid with login form
  return <SplitGridLoginView />;
}
